import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import 'react-datepicker/dist/react-datepicker.css';
import Tooltip from '../../components/utils/Tooltip';
// import MainLayout from '../../layouts/MainLayout';
import Loader from '../../components/utils/Loader';
import { useNavigate, Link } from "react-router-dom";

const MyIkigai = () => {
  const location = useLocation();
  const authState = useSelector(state => state.authReducer);
  const [tasks, setTasks] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  const fetchTasks = useCallback(() => {
    const config = {
      url: "/tasks/myikigai",
      method: "post",
      headers: { Authorization: authState.token },
      data: { userId: authState.user._id }
    };
    fetchData(config, { showSuccessToast: false }).then(data => setTasks(data.tasks));
  }, [authState.token, fetchData, authState.user._id]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchTasks();
  }, [authState.isLoggedIn, fetchTasks]);

  const handleView = (taskId) => {
    setSelectedTaskId(prevId => (prevId === taskId ? null : taskId));
  };

  const formatDateTime = (date) => {
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const groupTasks = (tasks) => {
    return tasks.reduce((acc, task) => {
      acc[task.ikigaiP] = acc[task.ikigaiP] || {};
      acc[task.ikigaiP][task.purpose] = acc[task.ikigaiP][task.purpose] || [];
      acc[task.ikigaiP][task.purpose].push(task);
      return acc;
    }, {});
  };

  const groupedTasks = groupTasks(tasks);

  return (
    <div className='min-h-screen p-4 bg-black  text-white'>
     <div className="flex flex-col w-full items-center text-white py-4 border-b border-pink-600 bg-black">
        <h2 className="text-lg lg:text-xl text-yellow-200">My Ikigai — My Pillars — My Actions</h2>

        <Link
          to="/tasks/add"
          className="flex items-center mt-4 gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg shadow-md transition"
        >
          💫 Add IKIGAI
        </Link>
      </div>
      <div className="my-4 mx-auto p-4 bg-black text-white rounded-lg shadow-lg">
        <div className="border-b border-gray-700 mb-4">
          {tasks.length > 0 && (
            <h3 className='text-lg text-yellow-400 font-semibold'>
              {tasks.length} Records Found
            </h3>
          )}
        </div>
        {loading ? (
          <Loader />
        ) : (
          <div>
            {tasks.length === 0 ? (
              <div className='w-full h-64 flex flex-col items-center justify-center text-yellow-400'>
                <h3 className='text-center text-xl'>Your Ikigai journey begins here 🌿 Discover what drives you — your passions, skills, and purpose. Start adding your first reflection.</h3>
                <Link
                  to="/tasks/add"
                  className="flex items-center mt-4 gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg shadow-md transition"
                >
                  🧘 Define My Purpose
                </Link>
              </div>
            ) : (
              Object.keys(groupedTasks).map(ikigaiP => (
                <div key={ikigaiP} className='mb-6'>
                  <p className='text-xl font-semibold bg-purple-800 text-yellow-300 p-2 rounded-t-md'>
                    {ikigaiP}
                  </p>
                  {Object.keys(groupedTasks[ikigaiP]).map(purpose => (
                    <div key={purpose} className='border border-gray-700 rounded-lg p-4 mb-4'>
                      <p className='text-lg font-medium'>{purpose}</p>
                      {groupedTasks[ikigaiP][purpose].map((task, index) => {
                        const showIkigaiP = index === 0 || task.ikigaiP !== groupedTasks[ikigaiP][purpose][index - 1].ikigaiP;
                        return (
                          <div key={task._id} className='bg-gray-800 my-2 p-3 rounded-md shadow-sm'>
                            <div className='flex justify-between items-center'>
                              {showIkigaiP && <span className='font-medium text-white'>{task.title}</span>}
                              <Tooltip text={"View details"} position={"top"}>
                                <span className='text-blue-400 cursor-pointer' onClick={() => handleView(task._id)}>
                                  <i className="fa-solid fa-eye"></i>
                                </span>
                              </Tooltip>
                            </div>
                            {selectedTaskId === task._id && (
                              <div className='bg-gray-700 text-gray-300 p-2 mt-2 rounded-md'>
                                <p><strong>Description:</strong> {task.description}</p>
                                <p><strong>Purpose:</strong> {task.purpose}</p>
                                <p><strong>Pillar:</strong> {task.ikigaiP}</p>
                                <p><strong>Date:</strong> {formatDateTime(task.taskDate)}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        )}
      </div>
      <footer className="bg-gray-900 border-t border-gray-800 text-gray-400 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-cyan-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-cyan-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-cyan-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-cyan-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MyIkigai;
